import { Box, Button, CircularProgress, FormControl, IconButton, InputAdornment, MenuItem, Select, Stack, Switch, Toolbar, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Loader } from "../../Helpers/Helpers";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { accountDetals, getIncomestatementInfo, getIncomestatementJson, incomestatementNetIncome, setMemorizeOrReset } from "../../API/nimbleAPI";
import { enqueueSnackbar } from "notistack";
import { exportFile } from "../../API/backend";
import { Interweave } from "interweave";
import clearIcon from "../../Images/clear-icon.png"
import PrintIcon from '@mui/icons-material/Print';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { ErrorBoundary } from "react-error-boundary";
import ViewReport from "../ViewReport/ViewReport";
import { saveAs } from "file-saver";

const BUDGET_API = import.meta.env.VITE_BUDGET_API;

const Report = () => {
    const { state } = useLocation();

    const [selectionsState, setSelectionsState] = useState(state);
    const navigate = useNavigate();
    const outletContext = useOutletContext();
    const { headerCorpId,  menuId, authId, urlInfo } = outletContext;

    const viewReportRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false)
    const [printLoading, setPrintLoading] = useState(false)
    const [settings, setSettings] = useState("");
    const [reportExport, setReportExport] = useState("");
    const [sendTo, setSendTo] = useState("");

    const [showDataGrid, setShowDataGrid] = useState(true)
    const [initialData, setInitialData] = useState();
    const [gridHasRows, setGridHasRows] = useState(true)
    const griReadyState = useState(false)

    const [isGridReady, setIsGridReady] = griReadyState

    const transformedColumnSelections = selectionsState?.columnSelections.map(
        ({ genType, selectedColumn, ...rest }) => ({
            displayId: genType,
            dbName: selectedColumn,
            ...rest,
        })
    );

    useEffect(() => {
        if (selectionsState.previousSelections?.display?.viewReport === "showReportInFullView") {
            console.log("Posting message showReportInFullView");
            window.parent.postMessage("HideMenu", "*");
        }

        getInitialData(selectionsState)
        const handleMessage = (event) => {
            if (event.data?.message === "refresh") {
                refreshReport();
            }
        };
    
        window.addEventListener("message", handleMessage);
    
        return () => {
            window.removeEventListener("message", handleMessage);
            window.parent.postMessage("ShowMenu", "*");
        };
    }, []);


    async function getInitialData(selectionsState) {
        let layoutKey = selectionsState.structurePayload.compressed_data
        let dataKey = selectionsState.dataPayload.compressed_data
        setLoading(true);
        let [layout, values] = await Promise.all([
            getRedisData(layoutKey),
            getRedisData(dataKey),
        ]);
        if (
            Object.values(values)
                .filter(Array.isArray)
                .every((a) => a.length === 0)
        ) {
            setGridHasRows(false);
            setIsGridReady(true)
        }
        setInitialData({ layout, values });
        setLoading(false);
    }

    async function getRedisData(jsonKey) {
        let res = await fetch(
        `${BUDGET_API}/get-json-object?json_key=${jsonKey}&decompress=true&compress_output=true`,
        );
        return res.json();
    }

    const handleSetting = async (e) => {
        setLoading(true);
        try {
            const value = e.target.value
            setSettings(value);
            const selectedFilters = selectionsState?.previousSelections;
            const selectedDisplaySettings = selectedFilters?.display;
            const memorizeFilteredSelections = {
                displayId: selectedFilters?.columnsBy?.displayId,
                displaySettings: 1,
                displayTextFormat: selectedDisplaySettings?.displayText,
                showNegativeFigures: selectedDisplaySettings?.negativeFigures?.formate,
                showNegativeFiguresFlag: selectedDisplaySettings?.negativeFigures?.colour ? 1 : 0,
                donotShowNoTransactionAccounts: selectedDisplaySettings?.showAccounts?.includes("don'tShowNoTransactionAccounts") ? 1 : 0,
                donotShowNullifiedAccounts: selectedDisplaySettings?.showAccounts?.includes("don'tShowNullifiedAccounts") ? 1 : 0,
                hideAccountCode: selectedDisplaySettings?.showAccounts?.includes("hideAccountCode") ? 1 : 0,
                dividedby1000: selectedDisplaySettings?.showFigures?.includes("dividedBy1000") ? 1 : 0,
                withoutcents: selectedDisplaySettings?.showFigures?.includes("withOutCents") ? 1 : 0,
                excludeZeroValues: selectedDisplaySettings?.showFigures?.includes("excludeZeroValues") ? 1 : 0,
                showReportinFullView: selectedDisplaySettings?.viewReport === "showReportInFullView" ? 1 : 0,
                viewTransaction: selectedFilters?.viewTransaction,
                layoutType: selectedFilters?.reportType,
                accrualCash: selectedFilters?.basis
            }

            const memorizePayload = { ...memorizeFilteredSelections, menuId: menuId, memorizeList: transformedColumnSelections || [] };
            const resetPayload = { menuId: menuId, displayId: selectionsState.displayId };

            const url = value === "Memorize" ? "ReportConfigMemorize" : "DeleteReportConfigMemorize";
            const payload = value === "Memorize" ? memorizePayload : resetPayload;
            const memorizeOrReset = await setMemorizeOrReset(url, payload);
            if (memorizeOrReset.statuscode !== 200) {
                enqueueSnackbar(`${memorizeOrReset.status}`, { variant: "warning" });
                setLoading(false)
                return;
            } else {
                setSettings(value);
                enqueueSnackbar(`${memorizeOrReset.status}`, { variant: "success" })
            };

        } catch (error) {
            enqueueSnackbar("Error while Setting", { variant: "error" })
        } finally {
            setLoading(false);
            setSettings("");
        };


    };

    const handleExportChange = async (event) => {

        try {
            setExportLoading(true);

            const selectedFormat = event.target.value;
            setReportExport(selectedFormat);

            const exportPayload = {
                structure_payload: selectionsState.structurePayload ,
                data_payload: selectionsState.dataPayload ,
                netincome: selectionsState.netincome ,
            };
            const endPoint = selectedFormat === "PDF" ? "download_pdf_report" : "download_excel_report";

            const { blob, headers } = await exportFile(endPoint, exportPayload);

            let filename = "income-statement";
            const disposition = headers.get("content-disposition");

            if (disposition) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            } else {
                filename += selectedFormat === "PDF" ? ".pdf" : ".xlsx";
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            enqueueSnackbar("Error while Export", { variant: "error" });
        } finally {
            setExportLoading(false);
            setReportExport("");
        }
    };

    const refreshReport = async () => {

        setLoading(true);
        setGridHasRows(true)
        setIsGridReady(false)

        try {
            const [
                incomestatementJson,
                incomestatementInfo,
                netIncome
            ] = await Promise.all([
                getIncomestatementJson(selectionsState.incomestatementJsonPayload),
                getIncomestatementInfo(selectionsState.incomestatementInfoPayload),
                incomestatementNetIncome(selectionsState.incomestatementInfoPayload)
            ]);
            // const incomestatementJson = await getIncomestatementJson(selectionsState.incomestatementJsonPayload);
            if (incomestatementJson.statuscode !== 200) {
                enqueueSnackbar(`${incomestatementJson.status}`, { variant: "warning" });
                setLoading(false);
                return
            };

            // const incomestatementInfo = await getIncomestatementInfo(selectionsState.incomestatementInfoPayload);
            if (incomestatementInfo.statuscode !== 200) {
                enqueueSnackbar(`${incomestatementInfo.status}`, { variant: "warning" });
                setLoading(false);
                return
            };

            // const netIncome = await incomestatementNetIncome(selectionsState.incomestatementInfoPayload);
            if (!netIncome) {
                enqueueSnackbar("Error while IncomestatementNetIncome ", { variant: "warning" });
                setLoading(false);
                return
            };
            let updatedSelectionState = {
                ...selectionsState,
                structurePayload: incomestatementJson,
                dataPayload: incomestatementInfo,
                netincome: netIncome,
            }
            setSelectionsState(updatedSelectionState);
            await getInitialData(updatedSelectionState)

        } catch (error) {
            enqueueSnackbar("Error While Refresh", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };


    const handlePrint = async () => {
        try {
            setPrintLoading(true);

            const exportPayload = {
                structure_payload: selectionsState.structurePayload ,
                data_payload: selectionsState.dataPayload ,
                netincome: selectionsState.netincome ,
            };

            const { blob } = await exportFile(
                "download_pdf_report",
                exportPayload
            );

            const pdfUrl = URL.createObjectURL(blob);

            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();

                setTimeout(() => {
                    URL.revokeObjectURL(pdfUrl);
                    document.body.removeChild(iframe);
                }, 1000);
            };
        } catch (error) {
            enqueueSnackbar("Error while printing", {
                variant: "error",
            });
        } finally {
            setPrintLoading(false);
        }
    };
    return (
        <>
            <Stack height={"100%"} width={"100%"} p={1} gap={2}>
                <Toolbar
                    disableGutters
                    sx={{
                        backgroundColor: "transparent",
                        minHeight: "50px !important",
                        // justifyContent: "flex-end",
                        gap:1
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ cursor: "pointer", ml: 0.5 }}
                        onClick={() => {
                            window.parent.postMessage("ShowMenu", "*")
                            window.parent.postMessage("incomeconfig", "*")
                        }}
                    >
                        <img src={clearIcon} alt="cancel" height={25} width={25} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />

                    <Button size="small"
                        onClick={()=>{
                            const finalData = viewReportRef.current?.getFinalData();
                            const blob = new Blob(
                                [JSON.stringify(finalData, null, 2)],
                                { type: "application/json;charset=utf-8" }
                            );
                            saveAs(blob, "report.json");
                        }}
                        endIcon={<DownloadForOfflineIcon />}
                        disabled={!gridHasRows}
                    >
                        Grid Source
                    </Button>
                    <Button size="small"
                        onClick={handlePrint}
                        endIcon={printLoading ? <CircularProgress size={12} /> :  <PrintIcon fontSize="small" sx={{ color: "#53656f" }} />}
                        disabled={printLoading || exportLoading || !gridHasRows}
                    >
                        Print
                    </Button>
                    <Button size="small" onClick={refreshReport} disabled={printLoading || exportLoading}>Refresh</Button>

                    <FormControl size="small" disabled={!gridHasRows}>
                        <Select
                            value={settings}
                            onChange={handleSetting}
                            variant="outlined"
                            displayEmpty
                            sx={{
                                px: 1,
                                height: 31,
                                width: 130,
                                bgcolor: "#b5dcf2",
                                "& fieldset": { border: "none" },
                                fontSize: 12.5,
                                fontWeight: 400
                            }}
                            renderValue={(selected) => selected || "Settings"}
                        >
                            <MenuItem value="Memorize">Memorize</MenuItem>
                            <MenuItem value="Reset">Reset</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" disabled={!gridHasRows}>
                        <Select
                            value={sendTo}
                            onChange={(e) => setSendTo(e.target.value)}
                            variant="outlined"
                            displayEmpty
                            sx={{
                                px: 1,
                                height: 31,
                                width: 130,
                                bgcolor: "#b5dcf2",
                                "& fieldset": { border: "none" },
                                fontSize: 12.5,
                                fontWeight: 400
                            }}
                            renderValue={(selected) => selected || "Send"}
                        >
                            <MenuItem value="Send">Send</MenuItem>
                            <MenuItem value="Schedule">Schedule</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" disabled={exportLoading || printLoading || !gridHasRows}>
                        <Select
                            value={reportExport}
                            // onChange={(e) => setReportExport(e.target.value)}
                            onChange={handleExportChange}
                            variant="outlined"
                            displayEmpty
                            sx={{
                                px: 1,
                                height: 31,
                                width: 130,
                                bgcolor: "#b5dcf2",
                                "& fieldset": { border: "none" },
                                fontSize: 13,
                                fontWeight: 400
                            }}
                            renderValue={(selected) => selected || "Export to"}y
                            startAdornment={exportLoading &&
                                <InputAdornment position="start">
                                    <CircularProgress size={12} />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="PDF">PDF</MenuItem>
                            <MenuItem value="Excel">Excel</MenuItem>
                        </Select>
                    </FormControl>

                    <Button size="small"
                        onClick={() => navigate(`/incomestatement/${urlInfo}/${authId}/${menuId}/${headerCorpId}/customization`,
                            {
                                // state: { userSelections: selectionsState?.previousSelections },
                                state: {
                                    ...state,
                                    userSelections: selectionsState?.previousSelections,
                                }
                            })}
                    >
                        Customize
                    </Button>

                    {import.meta.env.DEV &&
                        <Switch
                            checked={showDataGrid}
                            onChange={(e, c)=>{setShowDataGrid(c), setGridHasRows(true)}}
                        />
                    }
                </Toolbar>

             <ErrorBoundary
                FallbackComponent={({ error, resetErrorBoundary }) => {
                    return (
                        <Stack height={"100%"}>
                            <Box flexGrow={1} overflow={"auto"}>
                                {error.stack}
                            </Box>
                            <Button color="n-action" onClick={resetErrorBoundary}>
                                Retry
                            </Button>
                        </Stack>
                    );
                }}
            >
                {gridHasRows ?
                    <ViewReport 
                        ref={viewReportRef}
                        initialData={initialData}
                        selectionsState={selectionsState}
                        showDataGrid={showDataGrid} 
                        setGridHasRows={setGridHasRows}
                        griReadyState={griReadyState}
                    />
                    :
                    <Stack flexGrow={1} alignItems={"center"} justifyContent={"center"}>No Data Available</Stack>
                }
                
            </ErrorBoundary>

            </Stack >
            <Loader loading={loading || !isGridReady} />
        </>
    )
};
export default Report;